import React, { useState,useEffect } from "react";
import { FaTrash, FaEdit } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import "bootstrap/dist/css/bootstrap.min.css";

const UpdateHospitalPurchase = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Purchase form state
  const [purchaseForm, setPurchaseForm] = useState({
    poDate: "",
    poNumber: "",
    vendorName: "",
    department: "",
    discount: 0,
    tax: 0,
    paymentMode: "",
    advancePaid: 0,
    paymentStatus: "",
  });

  // Purchase items state
  const [purchaseItems, setPurchaseItems] = useState([]);

  const [newItem, setNewItem] = useState({
    itemName: "",
    quantity: "",
    unitPrice: "",
    uploadFile: null,
  });
  // eslint-disable-next-line
  const addNewItem = () => {
    if (newItem.itemName && newItem.quantity && newItem.unitPrice) {
      const newId =
        purchaseItems.length > 0
          ? Math.max(...purchaseItems.map((item) => item.id)) + 1
          : 1;
      setPurchaseItems([
        ...purchaseItems,
        {
          id: newId,
          itemName: newItem.itemName,
          quantity: parseInt(newItem.quantity),
          unitPrice: parseFloat(newItem.unitPrice),
          uploadFile: newItem.uploadFile,
        },
      ]);
      setNewItem({
        itemName: "",
        quantity: "",
        unitPrice: "",
        uploadFile: null,
      });
    }
  };

  const removeItem = (id) => {
    setPurchaseItems(purchaseItems.filter((item) => item.id !== id));
  };

  const updateItem = (id, field, value) => {
    setPurchaseItems(
      purchaseItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };
    //   Top to move
    useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load the existing purchase record
  useEffect(() => {
    const fetchPurchase = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${Apiurl}/hospital_purchase/${id}`);
        const data = response.data;
        setPurchaseForm({
          poDate: data.po_date || "",
          poNumber: data.purchase_id || "",
          vendorName: data.vendor_name || "",
          department: data.department || "",
          discount: data.discount_percentage || 0,
          tax: data.tax_percentage || 0,
          paymentMode: data.payment_mode || "",
          advancePaid: data.advance_paid || 0,
          paymentStatus: data.payment_status || "",
        });
        setPurchaseItems(
          (data.items || []).map((item, index) => ({
            id: index + 1,
            itemName: item.item_name,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            uploadFile: null,
          }))
        );
      } catch (error) {
        console.error("Error fetching purchase:", error.message);
        setError("Failed to load purchase record.");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchPurchase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  

  // Calculate totals
  const subtotal = purchaseItems.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0
  );
  const discountAmount = (subtotal * purchaseForm.discount) / 100;
  const taxAmount = ((subtotal - discountAmount) * purchaseForm.tax) / 100;
  const totalAmount = subtotal - discountAmount + taxAmount;
  const balanceDue = totalAmount - purchaseForm.advancePaid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!purchaseForm.vendorName || !purchaseForm.department || !purchaseForm.poDate) {
      setError("Please fill in PO Date, Vendor Name, and Department.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${Apiurl}/hospital_purchase/${id}`, {
        purchase_id: purchaseForm.poNumber,
        po_date: purchaseForm.poDate,
        vendor_name: purchaseForm.vendorName,
        department: purchaseForm.department,
        items: purchaseItems.map((item) => ({
          item_name: item.itemName,
          quantity: item.quantity,
          unit_price: item.unitPrice,
        })),
        discount_percentage: purchaseForm.discount,
        tax_percentage: purchaseForm.tax,
        sub_total: subtotal,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        net_total: totalAmount,
        payment_mode: purchaseForm.paymentMode,
        advance_paid: purchaseForm.advancePaid,
        balance: balanceDue,
        payment_status: purchaseForm.paymentStatus || "Pending",
      });
      navigate("/hospital-purchases");
    } catch (error) {
      console.error("Error updating purchase:", error.message);
      setError("Failed to update purchase. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/hospital-purchases");
  };

  if (loading) {
    return <div className="container-fluid py-4">Loading...</div>;
  }

  return (
    <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-start py-4">
      <div className="card w-100 p-4">
        <div className="mb-3">
          <h4 className="fw-bold">Update Purchase</h4>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Purchase Header */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">PO Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={purchaseForm.poDate}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      poDate: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">PO Number</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Auto Fill"
                  value={purchaseForm.poNumber}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      poNumber: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Vendor Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Vendor Name"
                  value={purchaseForm.vendorName}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      vendorName: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Department</label>
                <select
                  className="form-select"
                  value={purchaseForm.department}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      department: e.target.value,
                    })
                  }
                  required
                >
                  <option value="">Select Department</option>
                  <option value="IVF Lab">IVF Lab</option>
                  <option value="OT">OT</option>
                  <option value="Admin">Admin</option>
                  <option value="Emergency">Emergency</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Purchase List */}
        <div className="card mb-4">
          <h5 className="mb-2 m-3">Purchase List</h5>
          <div className="card-body">
            {/* Add New Item Row */}
            <div className="row mb-3 border-bottom pb-3">
              <div className="col-md-3 mb-3">
                <label className="form-label small">Item Name(s)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Enter Item Name"
                  value={newItem.itemName}
                  onChange={(e) =>
                    setNewItem({ ...newItem, itemName: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label small">
                  Quantity <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Enter Quantity"
                  value={newItem.quantity}
                  onChange={(e) =>
                    setNewItem({ ...newItem, quantity: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label small">Unit Price (₹)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Enter Amount"
                  value={newItem.unitPrice}
                  onChange={(e) =>
                    setNewItem({ ...newItem, unitPrice: e.target.value })
                  }
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label small">
                  Upload Invoice (Optional)
                </label>
                <input
                  type="file"
                  className="form-control form-control-sm"
                  onChange={(e) =>
                    setNewItem({ ...newItem, uploadFile: e.target.files[0] })
                  }
                />
              </div>
            </div>

            {/* Items Table */}
            <div className="table-responsive">
              <table className="table table-sm">
                <thead className="table-light">
                  <tr>
                    <th>S.No</th>
                    <th>Item Name(s)</th>
                    <th>
                      Quantity <span className="text-danger"></span>
                    </th>
                    <th>Unit Price (₹)</th>
                    <th>Upload File</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseItems.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.itemName}
                          onChange={(e) =>
                            updateItem(item.id, "itemName", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(
                              item.id,
                              "quantity",
                              parseInt(e.target.value)
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateItem(
                              item.id,
                              "unitPrice",
                              parseFloat(e.target.value)
                            )
                          }
                        />
                      </td>
                      <td>
                        <span className="text-muted">
                          {item.uploadFile ? item.uploadFile.name : "-"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeItem(item.id)}
                          >
                            <FaTrash />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Amount Summary */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Amount Summary</h6>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-2 mb-2">
                <label className="form-label small">Discount (%)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Enter Discount"
                  value={purchaseForm.discount}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      discount: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label small">Tax (GST %)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Select Discount"
                  value={purchaseForm.tax}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      tax: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="col-md-3 mb-2">
                <label className="form-label small">Payment Mode</label>
                <select
                  className="form-select form-select-sm"
                  value={purchaseForm.paymentMode}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      paymentMode: e.target.value,
                    })
                  }
                >
                  <option value="">Select Payment Mode</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <label className="form-label small">Advance Paid (₹)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Enter Advance Paid (₹)"
                  value={purchaseForm.advancePaid}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      advancePaid: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="col-md-3 mb-2">
                <label className="form-label small">Payment Status</label>
                <select
                  className="form-select form-select-sm"
                  value={purchaseForm.paymentStatus}
                  onChange={(e) =>
                    setPurchaseForm({
                      ...purchaseForm,
                      paymentStatus: e.target.value,
                    })
                  }
                >
                  <option value="">Select Status</option>
                  <option value="Paid">Paid</option>
                  <option value="Partially Paid">Partially Paid</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row">
              <div className="col-md-4 mb-2">
                <div className="card bg-light">
                  <div className="card-body text-center py-2">
                    <h6 className="mb-0">
                      Total Amount: ₹{totalAmount.toLocaleString()}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-2">
                <div className="card bg-success text-white">
                  <div className="card-body text-center py-2">
                    <h6 className="mb-0">
                      Advance Paid: ₹{purchaseForm.advancePaid.toLocaleString()}
                    </h6>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-2">
                <div className="card bg-danger text-white">
                  <div className="card-body text-center py-2">
                    <h6 className="mb-0">
                      Balance Due: ₹{balanceDue.toLocaleString()}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="text-center">
          <button
            type="button"
            className="btn btn-danger mx-2"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-primary mx-2">
            Print
          </button>
          <button
            type="button"
            className="btn btn-success mx-2"
            onClick={handleSubmit}
            style={{ backgroundColor: "#01AEBE" }}
            disabled={saving}
          >
            {saving ? "Saving..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateHospitalPurchase;